// Desktop-only side-by-side "before / after" diff for a proposal. Complements
// (does not replace) the always-available diff list and its selection
// checkboxes below it.
//
// This renders a real aligned diff — every row exists in both columns (blank
// on one side for a pure add/remove), unchanged items shown plainly for
// context, and only actual changes colored. It intentionally does NOT gate on
// `selected`: a checkbox being unticked isn't the same as "nothing changed" —
// deselected changes still render (dimmed, tagged "not selected") so the
// preview always answers "what is this proposal doing", while the dimming
// answers "and will it actually happen if I hit Apply".
//
// Only items_change / grouping / check_state ops have a meaningful "before"
// (they mutate an existing list/template/checklist); new_* and metadata ops
// are left to the diff list. Hidden below the `sm` breakpoint — on a
// narrow/mobile viewport there is no room for two columns.

import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import type { AiProposalOp, AiProposalTarget, ResourceKind } from "@checklist/shared";
import {
  fetchChecklist,
  fetchList,
  fetchTemplate,
  type ApiChecklist,
  type ApiList,
  type ApiTemplate,
} from "../../lib/api.js";
import { atomicKey, changeKey, type SelectionKey } from "./proposalSelection.js";
import { GROUP_HEADING_CLASS } from "./groupHeadingStyle.js";

interface PreviewItem {
  text: string;
  checked?: boolean;
}

interface PreviewSection {
  /** Undefined = the list's ungrouped bucket. Always set for template/checklist sections. */
  name?: string;
  items: PreviewItem[];
}

type ItemsChangeOp = Extract<AiProposalOp, { op: "items_change" }>;
type GroupingOp = Extract<AiProposalOp, { op: "grouping" }>;
type CheckStateOp = Extract<AiProposalOp, { op: "check_state" }>;

interface PreviewGroup {
  target: AiProposalTarget;
  ops: Array<
    | { opIndex: number; op: ItemsChangeOp }
    | { opIndex: number; op: GroupingOp }
    | { opIndex: number; op: CheckStateOp }
  >;
}

const normalize = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
const MOVE_SEP = " › ";

export function collectPreviewGroups(ops: AiProposalOp[]): PreviewGroup[] {
  const byKey = new Map<string, PreviewGroup>();
  ops.forEach((op, opIndex) => {
    if (op.op !== "items_change" && op.op !== "grouping" && op.op !== "check_state") return;
    const key = `${op.target.kind}:${op.target.id}`;
    let group = byKey.get(key);
    if (!group) {
      group = { target: op.target, ops: [] };
      byKey.set(key, group);
    }
    group.ops.push({ opIndex, op } as PreviewGroup["ops"][number]);
  });
  return [...byKey.values()];
}

function flattenList(list: ApiList): PreviewSection[] {
  return [
    { name: undefined, items: list.items.map((i) => ({ text: i.text })) },
    ...list.subgroups.map((g) => ({ name: g.name, items: g.items.map((i) => ({ text: i.text })) })),
  ];
}

function flattenChecklist(checklist: ApiChecklist): PreviewSection[] {
  const sections: PreviewSection[] = [];
  for (const section of checklist.sections) {
    sections.push({ name: section.name, items: section.items.map((i) => ({ text: i.text, checked: i.checked })) });
    for (const sub of section.subgroups) {
      sections.push({
        name: `${section.name}${MOVE_SEP}${sub.name}`,
        items: sub.items.map((i) => ({ text: i.text, checked: i.checked })),
      });
    }
  }
  return sections;
}

function flattenTemplate(template: ApiTemplate): PreviewSection[] {
  return template.sections.map((section) =>
    section.type === "inline"
      ? { name: section.name, items: section.items.map((i) => ({ text: i.text })) }
      : { name: section.name, items: [{ text: "(items from the linked list)" }] }
  );
}

async function fetchSnapshot(target: AiProposalTarget): Promise<PreviewSection[]> {
  if (target.kind === "list") return flattenList(await fetchList(target.id));
  if (target.kind === "checklist") return flattenChecklist(await fetchChecklist(target.id));
  return flattenTemplate(await fetchTemplate(target.id));
}

// ---- Row-based diff model ----
//
// Built by replaying every op against a mutable copy of the "before" section
// list, in proposal order. Each row keeps its own `selected` flag (from the
// checkbox state) purely for the dimmed/"not selected" treatment — it never
// gates whether a row appears.

type DiffStatus = "unchanged" | "added" | "removed" | "edited" | "moved-out" | "moved-in";

interface DiffRow {
  key: string;
  beforeText?: string;
  afterText?: string;
  status: DiffStatus;
  /** moved-out: destination group name. moved-in: source group name. */
  moveLabel?: string;
  checked?: boolean;
  beforeChecked?: boolean;
  checkChanged?: boolean;
  selected: boolean;
}

interface DiffSection {
  name?: string;
  rows: DiffRow[];
}

function findSection(sections: DiffSection[], name: string | undefined): DiffSection {
  let section = sections.find((s) => s.name === name);
  if (!section) {
    section = { name, rows: [] };
    sections.push(section);
  }
  return section;
}

function findRowByText(sections: DiffSection[], text: string): { section: DiffSection; row: DiffRow } | null {
  const n = normalize(text);
  for (const section of sections) {
    const row = section.rows.find((r) => r.afterText !== undefined && normalize(r.afterText) === n);
    if (row) return { section, row };
  }
  return null;
}

function applyItemsChange(
  sections: DiffSection[],
  op: ItemsChangeOp,
  opIndex: number,
  selected: Set<SelectionKey>
) {
  op.changes.forEach((change, changeIndex) => {
    const isSelected = selected.has(changeKey(opIndex, changeIndex));
    if (change.op === "add") {
      const sectionName = op.target.kind === "list" ? change.group : (change.group ?? sections[0]?.name);
      const section = findSection(sections, sectionName);
      section.rows.push({
        key: `add:${opIndex}:${changeIndex}`,
        afterText: change.text,
        status: "added",
        selected: isSelected,
      });
      return;
    }
    const found = findRowByText(sections, change.text);
    if (!found) return;
    if (change.op === "remove") {
      found.row.status = "removed";
      found.row.afterText = undefined;
    } else {
      found.row.status = "edited";
      found.row.afterText = change.newText ?? found.row.afterText;
    }
    found.row.selected = isSelected;
  });
}

function applyGrouping(sections: DiffSection[], opIndex: number, op: GroupingOp, selected: Set<SelectionKey>) {
  const isSelected = selected.has(atomicKey(opIndex));
  const prefix = op.section ? `${op.section}${MOVE_SEP}` : null;
  const inScope = (name: string | undefined) => !op.section || name === op.section || (prefix && name?.startsWith(prefix));

  const targetForText = new Map<string, string | undefined>();
  for (const g of op.subgroups) {
    const name = op.section ? `${op.section}${MOVE_SEP}${g.name}` : g.name;
    for (const text of g.items) targetForText.set(normalize(text), name);
  }
  const ungroupedName = op.target.kind === "list" ? undefined : op.section;
  for (const text of op.ungrouped) targetForText.set(normalize(text), ungroupedName);

  // Snapshot rows to move before mutating (we're about to push into new sections).
  const toMove: Array<{ from: DiffSection; row: DiffRow; targetName: string | undefined }> = [];
  for (const section of sections) {
    if (!inScope(section.name)) continue;
    for (const row of section.rows) {
      if (row.afterText === undefined) continue;
      const key = normalize(row.afterText);
      if (!targetForText.has(key)) continue;
      const targetName = targetForText.get(key);
      if (targetName === section.name) continue;
      toMove.push({ from: section, row, targetName });
    }
  }

  for (const { from, row, targetName } of toMove) {
    row.status = "moved-out";
    row.moveLabel = targetName ?? "Ungrouped";
    row.selected = isSelected;
    const movedText = row.afterText as string;
    row.afterText = undefined;
    const dest = findSection(sections, targetName);
    dest.rows.push({
      key: `${row.key}:moved-in`,
      afterText: movedText,
      status: "moved-in",
      moveLabel: from.name ?? "Ungrouped",
      checked: row.checked,
      selected: isSelected,
    });
  }
}

function applyCheckState(sections: DiffSection[], opIndex: number, op: CheckStateOp, selected: Set<SelectionKey>) {
  const isSelected = selected.has(atomicKey(opIndex));
  const checkedSet = new Set(op.checked.map(normalize));
  const uncheckedSet = new Set(op.unchecked.map(normalize));
  for (const section of sections) {
    for (const row of section.rows) {
      if (row.afterText === undefined) continue;
      const n = normalize(row.afterText);
      if (checkedSet.has(n) && row.beforeChecked !== true) {
        row.checked = true;
        row.checkChanged = true;
        row.selected = isSelected;
      } else if (uncheckedSet.has(n) && row.beforeChecked !== false) {
        row.checked = false;
        row.checkChanged = true;
        row.selected = isSelected;
      }
    }
  }
}

function buildDiff(before: PreviewSection[], group: PreviewGroup, selected: Set<SelectionKey>): DiffSection[] {
  const sections: DiffSection[] = before.map((s) => ({
    name: s.name,
    rows: s.items.map((item) => ({
      key: `${s.name ?? "_"}::${item.text}`,
      beforeText: item.text,
      afterText: item.text,
      status: "unchanged" as const,
      checked: item.checked,
      beforeChecked: item.checked,
      selected: true,
    })),
  }));

  for (const entry of group.ops) {
    if (entry.op.op === "items_change") applyItemsChange(sections, entry.op, entry.opIndex, selected);
    else if (entry.op.op === "grouping") applyGrouping(sections, entry.opIndex, entry.op, selected);
    else applyCheckState(sections, entry.opIndex, entry.op, selected);
  }

  return sections.filter((s) => s.rows.length > 0);
}

function targetLabel(kind: ResourceKind): string {
  return kind === "list" ? "List" : kind === "template" ? "Template" : "Checklist";
}

const STATUS_ROW_CLASS: Record<DiffStatus, string> = {
  unchanged: "",
  added: "bg-success/10",
  removed: "bg-danger/10",
  edited: "bg-warning/10",
  "moved-out": "bg-brand-50",
  "moved-in": "bg-brand-50",
};

const STATUS_TEXT_CLASS: Record<DiffStatus, string> = {
  unchanged: "text-ink",
  added: "text-success",
  removed: "text-danger line-through",
  edited: "text-warning",
  "moved-out": "text-brand-700 line-through decoration-brand-700/40",
  "moved-in": "text-brand-700",
};

function DiffRowView({ row }: { row: DiffRow }) {
  const dim = row.status !== "unchanged" && !row.selected;
  const beforeShown = row.status === "moved-in" ? undefined : row.beforeText;
  const afterShown = row.status === "moved-out" ? undefined : row.afterText;
  return (
    <div className={`grid grid-cols-2 gap-2 py-0.5 text-sm ${dim ? "opacity-45" : ""}`}>
      <div className={`min-w-0 truncate rounded px-1 ${STATUS_ROW_CLASS[row.status]}`} title={beforeShown}>
        {beforeShown ? (
          <span className={row.status === "unchanged" ? "text-ink" : STATUS_TEXT_CLASS[row.status]}>
            {row.beforeChecked ? "☑ " : ""}
            {beforeShown}
          </span>
        ) : null}
        {row.status === "moved-out" ? (
          <span className="ml-1 text-xs italic text-brand-600">→ {row.moveLabel}</span>
        ) : null}
      </div>
      <div className={`min-w-0 truncate rounded px-1 ${STATUS_ROW_CLASS[row.status]}`} title={afterShown}>
        {afterShown ? (
          <span className={row.status === "unchanged" ? "text-ink" : STATUS_TEXT_CLASS[row.status]}>
            {row.checked ? "☑ " : ""}
            {afterShown}
          </span>
        ) : null}
        {row.status === "moved-in" ? <span className="ml-1 text-xs italic text-brand-600">← {row.moveLabel}</span> : null}
        {row.checkChanged ? (
          <span className="ml-1 text-xs font-medium text-warning">{row.checked ? "marked done" : "marked not done"}</span>
        ) : null}
        {dim ? <span className="ml-1 text-xs text-muted">(not selected)</span> : null}
      </div>
    </div>
  );
}

function DiffSectionBlock({ section }: { section: DiffSection }) {
  return (
    <div>
      {section.name ? <div className={GROUP_HEADING_CLASS}>{section.name}</div> : null}
      <div className="divide-y divide-slate-100">
        {section.rows.map((row) => (
          <DiffRowView key={row.key} row={row} />
        ))}
      </div>
    </div>
  );
}

function hideUnchangedRows(diff: DiffSection[]): DiffSection[] {
  return diff
    .map((section) => ({ ...section, rows: section.rows.filter((row) => row.status !== "unchanged") }))
    .filter((section) => section.rows.length > 0);
}

export function ProposalPreview({ ops, selected }: { ops: AiProposalOp[]; selected: Set<SelectionKey> }) {
  const groups = useMemo(() => collectPreviewGroups(ops), [ops]);
  const [snapshots, setSnapshots] = useState<Record<string, PreviewSection[] | "loading" | "error">>({});
  const [hideUnchanged, setHideUnchanged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    for (const group of groups) {
      const key = `${group.target.kind}:${group.target.id}`;
      setSnapshots((prev) => (prev[key] ? prev : { ...prev, [key]: "loading" }));
      void fetchSnapshot(group.target)
        .then((sections) => {
          if (!cancelled) setSnapshots((prev) => ({ ...prev, [key]: sections }));
        })
        .catch(() => {
          if (!cancelled) setSnapshots((prev) => ({ ...prev, [key]: "error" }));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [groups]);

  if (groups.length === 0) return null;

  return (
    <div className="hidden space-y-2 sm:block">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setHideUnchanged((v) => !v)}
          aria-pressed={hideUnchanged}
          title={hideUnchanged ? "Show every line, including unchanged ones" : "Hide lines that did not change"}
          className="flex items-center gap-1.5 rounded-lg border border-line bg-white px-2 py-1 text-xs font-medium text-muted transition hover:bg-slate-50 hover:text-ink"
        >
          {hideUnchanged ? <Eye size={13} aria-hidden="true" /> : <EyeOff size={13} aria-hidden="true" />}
          {hideUnchanged ? "Show all lines" : "Hide unchanged"}
        </button>
      </div>
      <div className="space-y-3">
        {groups.map((group) => {
          const key = `${group.target.kind}:${group.target.id}`;
          const snapshot = snapshots[key];
          const label = targetLabel(group.target.kind).toLowerCase();
          if (snapshot === undefined || snapshot === "loading") {
            return (
              <div key={key} className="rounded-lg border border-line bg-slate-50 p-3 text-xs text-muted">
                Loading current {label}…
              </div>
            );
          }
          if (snapshot === "error") {
            return (
              <div key={key} className="rounded-lg border border-line bg-slate-50 p-3 text-xs text-danger">
                Could not load the current {label} to preview.
              </div>
            );
          }
          const fullDiff = buildDiff(snapshot, group, selected);
          const diff = hideUnchanged ? hideUnchangedRows(fullDiff) : fullDiff;
          return (
            <div key={key} className="overflow-x-auto rounded-lg border border-line">
              <div className="grid grid-cols-2 gap-2 border-b border-line bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted">
                <div>{targetLabel(group.target.kind)} now</div>
                <div>After changes</div>
              </div>
              <div className="min-w-[22rem] space-y-2 bg-white p-3">
                {diff.length === 0 ? (
                  <p className="text-xs text-muted">
                    {fullDiff.length === 0 ? "No changes to preview." : "No changed lines — everything here is unchanged."}
                  </p>
                ) : (
                  diff.map((section, i) => <DiffSectionBlock key={section.name ?? `_${i}`} section={section} />)
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
